import { DeviceTabletSpeaker } from '@phosphor-icons/react';
import { PAIRING_TTL_MS } from '@shh/shared';
import { Busy, Button, Surface, Text, TextField } from '@shh/ui';
import { useEffect, useMemo, useState } from 'react';
import { encode } from 'uqr';
import { createPairingCode, friendlyError } from '../data/api';
import { settingsText } from '../i18n/settings';

/** The address the shared device opens – the QR code carries the code, nothing to type (1.29). */
export const pairUrl = (code: string) => `${location.origin}/koppeln?code=${code}`;

/**
 * Pairing a shared device from a parent's phone: an optional name ("Küche"), then a 6-digit code
 * and the same code as a QR for the tablet's camera. Used in the settings and when setting up the
 * family (1.29, Mike 28.10.: "es fehlt auch die Anleitung zum Koppeln").
 */
export function PairingPanel() {
  const [code, setCode] = useState<{ code: string; expiresAt: number }>();
  const [now, setNow] = useState(0);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!code) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [code]);
  const left = code ? Math.max(0, Math.round((code.expiresAt - now) / 1000)) : 0;

  if (code && left > 0)
    return (
      <Surface
        tone="sunken"
        className="flex flex-col items-center gap-gap-sm text-center"
        role="status"
      >
        <Text tone="secondary">{settingsText.scanOrType(`${location.host}/koppeln`)}</Text>
        <QrCode value={pairUrl(code.code)} label={settingsText.qrLabel} />
        <span className="font-display text-4xl font-black tracking-widest tabular-nums">
          {code.code}
        </span>
        <Text size="sm" tone="muted">
          {settingsText.validFor(`${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`)}
        </Text>
      </Surface>
    );
  if (creating) return <Busy label={settingsText.creatingCode} />;
  return (
    <div className="flex flex-col gap-gap-sm">
      <div className="flex flex-wrap items-end gap-gap-sm">
        <TextField
          label={settingsText.deviceName}
          placeholder={settingsText.namePlaceholder}
          value={name}
          onChange={setName}
          maxLength={40}
        />
        <Button
          icon={DeviceTabletSpeaker}
          onPress={() => {
            setCreating(true);
            setError(undefined);
            void createPairingCode(name.trim() || undefined)
              .then(
                (c) => {
                  // counted from now on this device – its clock may differ from the server's
                  const received = Date.now();
                  setNow(received);
                  setCode({ code: c.code, expiresAt: received + PAIRING_TTL_MS });
                },
                (e: unknown) => setError(friendlyError(e)),
              )
              .finally(() => setCreating(false));
          }}
        >
          {settingsText.pairWall}
        </Button>
      </div>
      {error && <p className="text-sm font-semibold text-feedback-danger">{error}</p>}
    </div>
  );
}

/** A QR code drawn as SVG squares in the text colour – on a light tile, so every camera reads it. */
function QrCode({ value, label }: { value: string; label: string }) {
  const qr = useMemo(() => encode(value, { border: 2 }), [value]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${qr.size} ${qr.size}`}
      className="size-qr rounded-control bg-qr-paper text-qr-ink"
      shapeRendering="crispEdges"
    >
      {qr.data.flatMap((row, y) =>
        row.map((on, x) =>
          on ? (
            <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="currentColor" />
          ) : null,
        ),
      )}
    </svg>
  );
}
